import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { VendorProduct, Vendor, Review } from '../types'
import { mockVendors } from '../data/mockVendors'
import ReviewCard from '../components/ReviewCard'

export default function ProductDetail() {
  const { vendorId, productId } = useParams()
  const [product, setProduct] = useState<VendorProduct | null>(null)
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [reviews, setReviews] = useState<Review[]>([])
  const [selectedSize, setSelectedSize] = useState(0)
  const [selectedImage, setSelectedImage] = useState(0)
  const [showAllImages, setShowAllImages] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'lab-results' | 'specifications'>('overview')

  useEffect(() => {
    // In a real app, this would fetch from API
    const foundVendor = mockVendors.find(v => v.id === vendorId)
    if (foundVendor) {
      setVendor(foundVendor)
      // For demo, create a mock product based on vendor data
      const mockProduct: VendorProduct = {
        id: productId || 'demo-product',
        name: 'Premium 7OH Tablets',
        description: 'Our flagship 7-OH mitragynine tablets offer consistent potency and reliable effects. Each tablet contains precisely measured active compounds, lab-tested for purity and potency. Perfect for experienced users seeking standardized dosing.',
        extractType: '7-OH Mitragynine',
        type: 'tablet',
        sizes: [
          { weight: '10 tablets', price: 24.99, inStock: true, sku: 'TAB-10' },
          { weight: '25 tablets', price: 54.99, inStock: true, sku: 'TAB-25' },
          { weight: '50 tablets', price: 99.99, inStock: true, sku: 'TAB-50' },
          { weight: '100 tablets', price: 179.99, inStock: false, sku: 'TAB-100' }
        ],
        images: [
          '/api/placeholder/600/400',
          '/api/placeholder/600/400',
          '/api/placeholder/600/400'
        ],
        inStock: true,
        featured: true,
        categories: ['7OH Products', 'Tablets', 'Premium'],
        tags: ['lab-tested', 'premium', 'consistent-dosing', 'experienced-users'],
        dateAdded: new Date('2024-01-15'),
        salesCount: 156,
        rating: 4.7,
        reviewCount: 23,
        potency: '10mg per tablet',
        labResults: {
          '7oh': 6.2,
          mitragynine: 1.3,
          testDate: new Date('2024-01-10'),
          labName: 'ACS Laboratory'
        }
      }
      setProduct(mockProduct)
    }
  }, [vendorId, productId])

  if (!product || !vendor) {
    return (
      <div className="product-detail-loading">
        <h2>Loading product details...</h2>
      </div>
    )
  }

  const currentSize = product.sizes[selectedSize]

  return (
    <div className="product-detail">
      <div className="product-detail-container">
        
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/vendors">Vendors</Link>
          <span>›</span>
          <Link to={`/vendor/${vendor.id}`}>{vendor.name}</Link>
          <span>›</span>
          <span>{product.name}</span>
        </nav>

        <div className="product-layout">
          
          {/* Product Images */}
          <div className="product-images">
            <div className="main-image">
              <img 
                src={product.images[selectedImage]} 
                alt={product.name}
                className="main-product-image"
              />
              {product.featured && (
                <div className="featured-badge">Featured</div>
              )}
            </div>
            
            {product.images.length > 1 && (
              <div className="image-thumbnails">
                {product.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${product.name} view ${index + 1}`}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-info">
            <div className="vendor-link">
              <Link to={`/vendor/${vendor.id}`} className="vendor-name">
                {vendor.name}
                {vendor.verified && <span className="verified-badge">✓</span>}
              </Link>
            </div>
            
            <h1 className="product-title">{product.name}</h1>
            
            <div className="product-meta">
              <div className="rating-section">
                <div className="rating">
                  <span className="rating-score">{product.rating}</span>
                  <span className="rating-stars">{'★'.repeat(Math.floor(product.rating))}</span>
                  <span className="review-count">({product.reviewCount} reviews)</span>
                </div>
                <div className="sales-info">
                  <span className="sales-count">{product.salesCount} sold</span>
                </div>
              </div>
              
              {product.potency && (
                <div className="potency-info">
                  <strong>Potency:</strong> {product.potency}
                </div>
              )}
              
              <div className="product-type">
                <strong>Type:</strong> {product.extractType} • {product.type}
              </div>
            </div>

            {/* Size Selection */}
            <div className="size-selection">
              <h3>Select Size & Price</h3>
              <div className="size-options">
                {product.sizes.map((size, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSize(index)}
                    className={`size-option ${selectedSize === index ? 'selected' : ''} ${!size.inStock ? 'out-of-stock' : ''}`}
                    disabled={!size.inStock}
                  >
                    <div className="size-weight">{size.weight}</div>
                    <div className="size-price">${size.price}</div>
                    {!size.inStock && <div className="out-of-stock-label">Out of Stock</div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Purchase Section */}
            <div className="purchase-section">
              <div className="current-price">
                <span className="price">${currentSize.price}</span>
                <span className="price-per-unit">
                  (${(currentSize.price / parseInt(currentSize.weight)).toFixed(2)} per unit)
                </span>
              </div>
              
              <div className="purchase-buttons">
                <a
                  href={vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="buy-now-btn"
                >
                  Buy Now at {vendor.name}
                </a>
                <button className="add-to-wishlist-btn">
                  Add to Wishlist
                </button>
              </div>
              
              <div className="stock-status">
                {currentSize.inStock ? (
                  <span className="in-stock">✓ In Stock</span>
                ) : (
                  <span className="out-of-stock">⚠ Out of Stock</span>
                )}
              </div>
            </div>

            {/* Product Tags */}
            <div className="product-tags">
              {product.tags.map(tag => (
                <span key={tag} className="product-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="product-tabs">
          <div className="tab-nav">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'reviews', label: `Reviews (${product.reviewCount})` },
              { id: 'lab-results', label: 'Lab Results' },
              { id: 'specifications', label: 'Specifications' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-content">
                <h3>Product Description</h3>
                <p>{product.description}</p>
                
                <h3>Categories</h3>
                <div className="categories">
                  {product.categories.map(category => (
                    <span key={category} className="category-badge">{category}</span>
                  ))}
                </div>

                <h3>Key Features</h3>
                <ul className="features-list">
                  <li>Lab-tested for purity and potency</li>
                  <li>Consistent dosing in each unit</li>
                  <li>Third-party verified results</li>
                  <li>Premium quality ingredients</li>
                </ul>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="reviews-content">
                <div className="reviews-summary">
                  <h3>Customer Reviews</h3>
                  <div className="rating-breakdown">
                    <div className="overall-rating">
                      <span className="rating-number">{product.rating}</span>
                      <div className="rating-stars">{'★'.repeat(Math.floor(product.rating))}</div>
                      <span className="total-reviews">{product.reviewCount} reviews</span>
                    </div>
                  </div>
                </div>
                
                <div className="reviews-list">
                  <p>Product-specific reviews would be displayed here</p>
                  <Link to={`/reviews?vendor=${vendor.id}&product=${product.id}`} className="view-all-reviews">
                    View All Product Reviews →
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'lab-results' && (
              <div className="lab-results-content">
                {product.labResults ? (
                  <div className="lab-results">
                    <h3>Third-Party Lab Analysis</h3>
                    <div className="lab-info">
                      <div className="lab-details">
                        <strong>Lab:</strong> {product.labResults.labName}<br/>
                        <strong>Test Date:</strong> {new Date(product.labResults.testDate).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="alkaloid-content">
                      <div className="alkaloid-item">
                        <span className="alkaloid-name">7-OH Mitragynine</span>
                        <span className="alkaloid-value">{product.labResults['7oh']}%</span>
                      </div>
                      <div className="alkaloid-item">
                        <span className="alkaloid-name">Mitragynine</span>
                        <span className="alkaloid-value">{product.labResults.mitragynine}%</span>
                      </div>
                    </div>
                    
                    <div className="lab-disclaimer">
                      <p><em>Lab results are provided by third-party testing facilities and represent the specific batch tested. Individual results may vary.</em></p>
                    </div>
                  </div>
                ) : (
                  <div className="no-lab-results">
                    <p>Lab results are not available for this product.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="specifications-content">
                <h3>Product Specifications</h3>
                <div className="spec-table">
                  <div className="spec-row">
                    <span className="spec-label">Product Type:</span>
                    <span className="spec-value">{product.type}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Extract Type:</span>
                    <span className="spec-value">{product.extractType}</span>
                  </div>
                  {product.potency && (
                    <div className="spec-row">
                      <span className="spec-label">Potency:</span>
                      <span className="spec-value">{product.potency}</span>
                    </div>
                  )}
                  <div className="spec-row">
                    <span className="spec-label">Available Sizes:</span>
                    <span className="spec-value">{product.sizes.map(s => s.weight).join(', ')}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Added Date:</span>
                    <span className="spec-value">{new Date(product.dateAdded).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="related-products">
          <h3>More from {vendor.name}</h3>
          <div className="related-products-grid">
            <div className="related-product-placeholder">
              <p>Other products from this vendor would be displayed here</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .product-detail {
          min-height: 100vh;
          background: #f5f5f5;
        }

        .product-detail-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #2d3748;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 30px;
          font-size: 14px;
          color: #a0aec0;
        }

        .breadcrumb a {
          color: #2c5530;
          text-decoration: none;
        }

        .breadcrumb a:hover {
          text-decoration: underline;
        }

        .product-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        @media (max-width: 768px) {
          .product-layout {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }

        .product-images {
          position: sticky;
          top: 20px;
          height: fit-content;
        }

        .main-image {
          position: relative;
          margin-bottom: 15px;
        }

        .main-product-image {
          width: 100%;
          height: 400px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #4a5568;
        }

        .featured-badge {
          position: absolute;
          top: 15px;
          left: 15px;
          background: linear-gradient(135deg, #ff6b6b, #ffa726);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .image-thumbnails {
          display: flex;
          gap: 10px;
          overflow-x: auto;
        }

        .thumbnail {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 4px;
          border: 2px solid transparent;
          cursor: pointer;
          transition: border-color 0.2s ease;
        }

        .thumbnail:hover {
          border-color: #2c5530;
        }

        .thumbnail.active {
          border-color: #2c5530;
        }

        .vendor-link {
          margin-bottom: 10px;
        }

        .vendor-name {
          color: #a0aec0;
          text-decoration: none;
          font-size: 16px;
        }

        .vendor-name:hover {
          color: #2c5530;
        }

        .verified-badge {
          color: #4caf50;
          margin-left: 5px;
        }

        .product-title {
          font-size: 2.5em;
          color: #2c5530;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .product-meta {
          margin-bottom: 30px;
        }

        .rating-section {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 15px;
        }

        .rating {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rating-score {
          font-size: 20px;
          font-weight: bold;
          color: #2c5530;
        }

        .rating-stars {
          color: #ffa726;
          font-size: 18px;
        }

        .review-count {
          color: #a0aec0;
          font-size: 14px;
        }

        .sales-count {
          color: #a0aec0;
          font-size: 14px;
        }

        .potency-info, .product-type {
          margin-bottom: 10px;
          color: #e2e8f0;
        }

        .size-selection {
          margin-bottom: 30px;
        }

        .size-selection h3 {
          margin-bottom: 15px;
          color: #2c5530;
        }

        .size-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
        }

        .size-option {
          padding: 15px;
          border: 2px solid #eee;
          border-radius: 8px;
          background: #2d3748;
          cursor: pointer;
          text-align: center;
          transition: all 0.2s ease;
          position: relative;
        }

        .size-option:hover {
          border-color: #2c5530;
        }

        .size-option.selected {
          border-color: #2c5530;
          background: #f8fdf8;
        }

        .size-option.out-of-stock {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .size-weight {
          font-weight: 600;
          margin-bottom: 5px;
        }

        .size-price {
          color: #2c5530;
          font-weight: bold;
          font-size: 18px;
        }

        .out-of-stock-label {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.9);
          color: #d32f2f;
          font-weight: bold;
          font-size: 12px;
        }

        .purchase-section {
          margin-bottom: 30px;
        }

        .current-price {
          margin-bottom: 20px;
        }

        .price {
          font-size: 2em;
          font-weight: bold;
          color: #2c5530;
        }

        .price-per-unit {
          color: #a0aec0;
          font-size: 14px;
          margin-left: 10px;
        }

        .purchase-buttons {
          display: flex;
          gap: 15px;
          margin-bottom: 15px;
        }

        .buy-now-btn {
          flex: 2;
          padding: 15px 20px;
          background: linear-gradient(135deg, #2c5530, #4caf50);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          text-align: center;
          font-weight: 600;
          transition: transform 0.2s ease;
        }

        .buy-now-btn:hover {
          transform: translateY(-2px);
        }

        .add-to-wishlist-btn {
          flex: 1;
          padding: 15px 20px;
          background: #2d3748;
          color: #2c5530;
          border: 2px solid #2c5530;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-to-wishlist-btn:hover {
          background: #2c5530;
          color: white;
        }

        .stock-status {
          font-size: 14px;
        }

        .in-stock {
          color: #4caf50;
        }

        .out-of-stock {
          color: #d32f2f;
        }

        .product-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .product-tag {
          background: #e8f5e8;
          color: #2c5530;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .product-tabs {
          border-top: 1px solid #eee;
          margin-top: 40px;
          padding-top: 30px;
        }

        .tab-nav {
          display: flex;
          border-bottom: 2px solid #eee;
          margin-bottom: 30px;
        }

        .tab-button {
          padding: 15px 25px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 16px;
          color: #a0aec0;
          transition: all 0.3s ease;
        }

        .tab-button:hover {
          color: #2c5530;
        }

        .tab-button.active {
          color: #2c5530;
          border-bottom-color: #2c5530;
        }

        .tab-content {
          min-height: 300px;
        }

        .overview-content h3 {
          color: #2c5530;
          margin-bottom: 15px;
        }

        .categories {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }

        .category-badge {
          background: #2c5530;
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
        }

        .features-list {
          list-style: none;
          padding: 0;
        }

        .features-list li {
          padding: 8px 0;
          border-bottom: 1px solid #eee;
        }

        .features-list li:before {
          content: "✓";
          color: #4caf50;
          font-weight: bold;
          margin-right: 10px;
        }

        .reviews-summary {
          margin-bottom: 30px;
        }

        .rating-breakdown {
          margin-top: 15px;
        }

        .overall-rating {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .rating-number {
          font-size: 3em;
          font-weight: bold;
          color: #2c5530;
        }

        .total-reviews {
          color: #a0aec0;
        }

        .view-all-reviews {
          color: #2c5530;
          text-decoration: none;
          font-weight: 600;
        }

        .lab-results h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .lab-info {
          background: #f9f9f9;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .alkaloid-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .alkaloid-item {
          background: #2d3748;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #4a5568;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .alkaloid-name {
          font-weight: 600;
        }

        .alkaloid-value {
          font-size: 1.5em;
          font-weight: bold;
          color: #2c5530;
        }

        .lab-disclaimer {
          background: #2d37483cd;
          border: 1px solid #ffeaa7;
          padding: 15px;
          border-radius: 8px;
          font-size: 14px;
        }

        .no-lab-results {
          text-align: center;
          padding: 40px;
          color: #a0aec0;
        }

        .spec-table {
          background: #2d3748;
          border-radius: 8px;
          overflow: hidden;
        }

        .spec-row {
          display: flex;
          justify-content: space-between;
          padding: 15px 20px;
          border-bottom: 1px solid #eee;
        }

        .spec-row:last-child {
          border-bottom: none;
        }

        .spec-label {
          font-weight: 600;
          color: #e2e8f0;
        }

        .spec-value {
          color: #a0aec0;
        }

        .related-products {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 1px solid #eee;
        }

        .related-products h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .related-product-placeholder {
          background: #f9f9f9;
          padding: 40px;
          text-align: center;
          border-radius: 8px;
          color: #a0aec0;
        }
      `}</style>
    </div>
  )
}